// pages/Dashboard/Billing.js
import { useState, useMemo } from 'react';
import {
  FaPlus,
  FaTrash,
  FaMoneyBillWave,
  FaMobileAlt,
  FaQrcode,
  FaCreditCard,
  FaTags,
  FaPercentage,
  FaPrint,
  FaSave,
  FaUserCircle,
  FaSearch,
  FaReceipt,
} from 'react-icons/fa';
import '../Css/Billing.css';

const SERVICE_CATALOG = [
  { id: 's1', name: 'Haircut', price: 300 },
  { id: 's2', name: 'Hair Color', price: 1200 },
  { id: 's3', name: 'Facial', price: 900 },
  { id: 's4', name: 'Manicure', price: 400 },
  { id: 's5', name: 'Pedicure', price: 500 },
  { id: 's6', name: 'Hair Spa', price: 800 },
];

const STAFF_LIST = ['Riya Sharma', 'Ankit Kapoor', 'Meena Iyer', 'Sohail Khan'];

const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', icon: <FaMoneyBillWave /> },
  { key: 'upi', label: 'UPI', icon: <FaMobileAlt /> },
  { key: 'qr', label: 'QR Code', icon: <FaQrcode /> },
  { key: 'card', label: 'Card', icon: <FaCreditCard /> },
];

let idCounter = 1;

function Billing() {
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [items, setItems] = useState([
    { id: idCounter++, serviceId: 's1', staff: STAFF_LIST[0], qty: 1 },
  ]);
  const [search, setSearch] = useState('');
  const [discountType, setDiscountType] = useState('percent'); // percent | flat
  const [discountValue, setDiscountValue] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [taxPercent] = useState(5);
  const [splitPayments, setSplitPayments] = useState([{ method: 'cash', amount: '' }]);

  const filteredCatalog = useMemo(
    () =>
      SERVICE_CATALOG.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  const addItem = (service) => {
    setItems((prev) => [
      ...prev,
      { id: idCounter++, serviceId: service.id, staff: STAFF_LIST[0], qty: 1 },
    ]);
    setSearch('');
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, [field]: value } : i))
    );
  };

  const getService = (serviceId) =>
    SERVICE_CATALOG.find((s) => s.id === serviceId);

  const subtotal = items.reduce((sum, item) => {
    const svc = getService(item.serviceId);
    return sum + (svc ? svc.price * item.qty : 0);
  }, 0);

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (code === 'SALON10') {
      setCouponApplied({ code, percent: 10 });
    } else if (code === 'FIRSTVISIT') {
      setCouponApplied({ code, percent: 15 });
    } else {
      setCouponApplied(null);
      alert('Invalid coupon code');
    }
  };

  const manualDiscount =
    discountType === 'percent'
      ? (subtotal * Number(discountValue || 0)) / 100
      : Number(discountValue || 0);

  const couponDiscount = couponApplied
    ? (subtotal * couponApplied.percent) / 100
    : 0;

  const totalDiscount = manualDiscount + couponDiscount;
  const afterDiscount = Math.max(subtotal - totalDiscount, 0);
  const taxAmount = (afterDiscount * taxPercent) / 100;
  const grandTotal = afterDiscount + taxAmount;

  const totalPaid = splitPayments.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );
  const dueAmount = Math.max(grandTotal - totalPaid, 0);
  const changeDue = Math.max(totalPaid - grandTotal, 0);

  const addSplitRow = () => {
    setSplitPayments((prev) => [...prev, { method: 'cash', amount: '' }]);
  };

  const removeSplitRow = (index) => {
    setSplitPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSplitRow = (index, field, value) => {
    setSplitPayments((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const fillRemaining = (index) => {
    const otherTotal = splitPayments.reduce(
      (sum, p, i) => (i === index ? sum : sum + Number(p.amount || 0)),
      0
    );
    const remaining = Math.max(grandTotal - otherTotal, 0).toFixed(2);
    updateSplitRow(index, 'amount', remaining);
  };

  const handleGenerate = () => {
    if (!clientName.trim()) {
      alert('Please enter client name');
      return;
    }
    if (items.length === 0) {
      alert('Add at least one service');
      return;
    }
    alert('Invoice generated successfully!');
  };

  return (
    <div className="billing-page">
      <div className="billing-header">
        <div>
          <h2>New Invoice</h2>
          <p>Create a fast, accurate bill for your client</p>
        </div>
        <div className="header-actions">
          <button className="btn-outline">
            <FaPrint /> Print
          </button>
          <button className="btn-primary" onClick={handleGenerate}>
            <FaReceipt /> Generate Invoice
          </button>
        </div>
      </div>

      <div className="billing-grid">
        {/* LEFT: Builder */}
        <div className="billing-main">
          {/* Client Info */}
          <div className="panel">
            <h3 className="panel-title">Client Details</h3>
            <div className="client-row">
              <div className="input-group">
                <FaUserCircle className="input-icon" />
                <input
                  type="text"
                  placeholder="Client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
              </div>
              <div className="input-group">
                <FaMobileAlt className="input-icon" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Add Services */}
          <div className="panel">
            <h3 className="panel-title">Add Services / Products</h3>
            <div className="input-group search-box">
              <FaSearch className="input-icon" />
              <input
                type="text"
                placeholder="Search services (e.g. Haircut, Facial)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {search && (
              <div className="search-results">
                {filteredCatalog.map((svc) => (
                  <div
                    key={svc.id}
                    className="search-result-item"
                    onClick={() => addItem(svc)}
                  >
                    <span>{svc.name}</span>
                    <span className="svc-price">₹{svc.price}</span>
                  </div>
                ))}
                {filteredCatalog.length === 0 && (
                  <div className="search-result-empty">No services found</div>
                )}
              </div>
            )}

            <div className="items-table">
              <div className="items-table-head">
                <span>Service</span>
                <span>Staff</span>
                <span>Qty</span>
                <span>Price</span>
                <span>Total</span>
                <span></span>
              </div>
              {items.map((item) => {
                const svc = getService(item.serviceId);
                return (
                  <div className="items-table-row" key={item.id}>
                    <select
                      value={item.serviceId}
                      onChange={(e) =>
                        updateItem(item.id, 'serviceId', e.target.value)
                      }
                    >
                      {SERVICE_CATALOG.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={item.staff}
                      onChange={(e) =>
                        updateItem(item.id, 'staff', e.target.value)
                      }
                    >
                      {STAFF_LIST.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateItem(item.id, 'qty', Number(e.target.value))
                      }
                    />
                    <span className="cell-price">₹{svc?.price}</span>
                    <span className="cell-total">
                      ₹{svc ? svc.price * item.qty : 0}
                    </span>
                    <button
                      className="row-delete-btn"
                      onClick={() => removeItem(item.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="items-empty">No services added yet</div>
              )}
            </div>
          </div>

          {/* Discounts & Coupons */}
          <div className="panel">
            <h3 className="panel-title">Discounts & Offers</h3>
            <div className="discount-row">
              <div className="discount-type-toggle">
                <button
                  className={discountType === 'percent' ? 'active' : ''}
                  onClick={() => setDiscountType('percent')}
                >
                  <FaPercentage /> %
                </button>
                <button
                  className={discountType === 'flat' ? 'active' : ''}
                  onClick={() => setDiscountType('flat')}
                >
                  ₹ Flat
                </button>
              </div>
              <input
                type="number"
                min="0"
                placeholder="Discount value"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="discount-input"
              />
            </div>

            <div className="coupon-row">
              <div className="input-group">
                <FaTags className="input-icon" />
                <input
                  type="text"
                  placeholder="Coupon code (e.g. SALON10)"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
              </div>
              <button className="btn-outline" onClick={applyCoupon}>
                Apply
              </button>
            </div>
            {couponApplied && (
              <div className="coupon-applied">
                Coupon <strong>{couponApplied.code}</strong> applied —{' '}
                {couponApplied.percent}% off
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="panel">
            <h3 className="panel-title">Payment</h3>
            <p className="panel-subtitle">
              Accept full or partial payment, split across methods
            </p>

            {splitPayments.map((p, index) => (
              <div className="split-row" key={index}>
                <div className="method-select">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.key}
                      className={`method-chip ${
                        p.method === m.key ? 'active' : ''
                      }`}
                      onClick={() => updateSplitRow(index, 'method', m.key)}
                      type="button"
                    >
                      {m.icon} {m.label}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="0"
                  placeholder="Amount"
                  value={p.amount}
                  onChange={(e) =>
                    updateSplitRow(index, 'amount', e.target.value)
                  }
                  className="split-amount-input"
                />
                <button
                  className="link-btn"
                  onClick={() => fillRemaining(index)}
                  type="button"
                >
                  Fill remaining
                </button>
                {splitPayments.length > 1 && (
                  <button
                    className="row-delete-btn"
                    onClick={() => removeSplitRow(index)}
                    type="button"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}

            <button className="add-split-btn" onClick={addSplitRow} type="button">
              <FaPlus /> Add another payment method
            </button>
          </div>
        </div>

        {/* RIGHT: Summary */}
        <div className="billing-summary">
          <div className="summary-panel">
            <h3 className="panel-title">Invoice Summary</h3>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {manualDiscount > 0 && (
              <div className="summary-row discount">
                <span>Discount</span>
                <span>-₹{manualDiscount.toFixed(2)}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="summary-row discount">
                <span>Coupon ({couponApplied.code})</span>
                <span>-₹{couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row">
              <span>Tax ({taxPercent}%)</span>
              <span>₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row grand-total">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row">
              <span>Amount Paid</span>
              <span>₹{totalPaid.toFixed(2)}</span>
            </div>
            {dueAmount > 0 && (
              <div className="summary-row due">
                <span>Balance Due</span>
                <span>₹{dueAmount.toFixed(2)}</span>
              </div>
            )}
            {changeDue > 0 && (
              <div className="summary-row change">
                <span>Change to Return</span>
                <span>₹{changeDue.toFixed(2)}</span>
              </div>
            )}

            <button className="btn-primary full-width" onClick={handleGenerate}>
              <FaSave /> Save & Generate Invoice
            </button>

            {dueAmount > 0 && (
              <p className="due-note">
                This will be saved as a <strong>partial payment</strong>. Balance
                can be collected later.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Billing;